import { ExternalLink } from 'lucide-react';
import { useState } from 'react';

import { buttonClass } from '@/modules/signup-flow/components/layout/motion';
import {
  StatusDot,
  StepActions,
} from '@/modules/signup-flow/components/layout/SignupLayoutParts';
import { StepHeader } from '@/modules/signup-flow/components/layout/StepHeader';
import {
  SIGN_UP_FLOW_STEPS,
  signupFlowStep,
} from '@/modules/signup-flow/signupFlowSteps';
import { Button } from '@/modules/ui/components/button/Button';

import { SlackLogo } from './SlackLogo';
import { SlackTitle } from './SlackTitle';

interface SlackConnectProps {
  isSubmitting?: boolean;
  onBack: () => void;
  /** Starts OAuth in a new tab; this page stays and waits for the connection. */
  onConnect: () => void;
  onSkip: () => void;
}

/**
 * Slack step, connect phase: the Integrations row, Connect as the primary
 * action. After Connect the step waits for the Slack tab, and Skip stays
 * available: a workspace that needs admin approval never sends the user back.
 */
export const SlackConnect = ({
  isSubmitting = false,
  onBack,
  onConnect,
  onSkip,
}: SlackConnectProps) => {
  const [waiting, setWaiting] = useState(false);

  const connect = () => {
    setWaiting(true);
    onConnect();
  };

  return (
    <div className="flex flex-col gap-8">
      <StepHeader
        step={signupFlowStep.slack}
        totalSteps={SIGN_UP_FLOW_STEPS}
        title={<SlackTitle>Bring Finlay into</SlackTitle>}
        subtitle="Review candidates and confirm interviews from the channel your team already reads. You can change this later under Integrations."
      />

      <ul className="divide-border divide-y border-y">
        <li className="grid gap-3 py-5 md:grid-cols-[24px_minmax(0,1fr)] md:items-center">
          <SlackLogo aria-hidden className="size-5" />
          <div className="flex flex-col gap-0.5">
            <span className="flex items-center gap-3 font-medium">
              Slack
              <StatusDot tone="neutral">
                {waiting ? 'Waiting for Slack' : 'Not connected'}
              </StatusDot>
            </span>
            <span className="text-muted-foreground max-w-[48ch] text-[13px] leading-[18px]">
              {waiting
                ? 'Finish connecting in the Slack tab. This page updates when you come back.'
                : 'Takes under a minute. Finlay only posts in channels you choose.'}
            </span>
          </div>
        </li>
      </ul>

      <p className="text-muted-foreground max-w-[60ch] text-[13px] leading-[18px]">
        Does your Slack workspace need admin approval? Send the request in
        Slack, then skip this step. Once your admin approves Finlay, connect
        Slack from Integrations.
      </p>

      <StepActions>
        <Button
          variant="ghost"
          className={buttonClass}
          disabled={isSubmitting}
          onClick={onBack}
        >
          Back
        </Button>
        {/* Skip stays quiet text: people skipped Slack when it sat in the
            primary slot, so Connect owns that slot. */}
        <Button
          variant="link"
          className="text-muted-foreground hover:text-foreground h-8 rounded-md px-1 py-0 text-[13px] sm:mr-1"
          loading={isSubmitting}
          onClick={onSkip}
        >
          Skip for now
        </Button>
        <Button
          variant={waiting ? 'outline' : undefined}
          className={buttonClass}
          disabled={isSubmitting}
          onClick={connect}
        >
          <SlackLogo className="size-4" />
          {waiting ? 'Reopen Slack' : 'Connect Slack'}
          <ExternalLink aria-hidden className="size-3.5" />
        </Button>
      </StepActions>
    </div>
  );
};
